import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { siteConfig } from "../Datas/productData";

export const CartContext = createContext(null);

const readStoredArray = (key) => {
  if (typeof window === "undefined") return [];

  try {
    const storedValue = window.localStorage.getItem(key);
    const parsedValue = storedValue ? JSON.parse(storedValue) : [];
    return Array.isArray(parsedValue) ? parsedValue : [];
  } catch {
    return [];
  }
};

export const CartProvider = ({ children }) => {
  const cartStorageKey = siteConfig.storage.cartKey;
  const wishlistStorageKey = siteConfig.storage.wishlistKey;
  const [cartItems, setCartItems] = useState(() =>
    readStoredArray(cartStorageKey)
      .filter((item) => item && Number.isFinite(Number(item.id)))
      .map((item) => ({
        id: Number(item.id),
        size: typeof item.size === "string" ? item.size : "",
        color: typeof item.color === "string" ? item.color : "",
        quantity: Math.max(1, Number(item.quantity) || 1),
      }))
  );
  const [wishlistItems, setWishlistItems] = useState(() =>
    [...new Set(
      readStoredArray(wishlistStorageKey)
        .map(Number)
        .filter(Number.isFinite)
    )]
  );

  useEffect(() => {
    try {
      window.localStorage.setItem(cartStorageKey, JSON.stringify(cartItems));
    } catch {
      // State remains available for the current session when storage is blocked.
    }
  }, [cartItems, cartStorageKey]);

  useEffect(() => {
    try {
      window.localStorage.setItem(
        wishlistStorageKey,
        JSON.stringify(wishlistItems)
      );
    } catch {
      // State remains available for the current session when storage is blocked.
    }
  }, [wishlistItems, wishlistStorageKey]);

  const addToCart = useCallback((productId, quantity = 1, size = "", color = "") => {
    const normalizedProductId = Number(productId);
    const amount = Math.max(1, Number(quantity) || 1);

    setCartItems((currentItems) => {
      const matchingItem = currentItems.find(
        (item) =>
          item.id === normalizedProductId && item.size === size && item.color === color
      );

      if (matchingItem) {
        return currentItems.map((item) =>
          item.id === normalizedProductId && item.size === size && item.color === color
            ? { ...item, quantity: item.quantity + amount }
            : item
        );
      }

      return [
        ...currentItems,
        { id: normalizedProductId, size, color, quantity: amount },
      ];
    });
  }, []);

  const removeFromCart = useCallback((productId, size = "", color = "") => {
    const normalizedProductId = Number(productId);
    setCartItems((currentItems) =>
      currentItems.filter(
        (item) =>
          !(
            item.id === normalizedProductId &&
            item.size === size &&
            item.color === color
          )
      )
    );
  }, []);

  const updateQuantity = useCallback(
    (productId, size = "", color = "", quantity = 1) => {
      const normalizedProductId = Number(productId);
      const amount = Math.max(1, Number(quantity) || 1);
      setCartItems((currentItems) =>
        currentItems.map((item) =>
          item.id === normalizedProductId && item.size === size && item.color === color
            ? { ...item, quantity: amount }
            : item
        )
      );
    },
    []
  );

  const clearCart = useCallback(() => setCartItems([]), []);

  const addToWishlist = useCallback((productId) => {
    const normalizedProductId = Number(productId);
    setWishlistItems((currentItems) =>
      currentItems.includes(normalizedProductId)
        ? currentItems
        : [...currentItems, normalizedProductId]
    );
  }, []);

  const removeFromWishlist = useCallback((productId) => {
    const normalizedProductId = Number(productId);
    setWishlistItems((currentItems) =>
      currentItems.filter((itemId) => itemId !== normalizedProductId)
    );
  }, []);

  const toggleWishlist = useCallback((productId) => {
    const normalizedProductId = Number(productId);
    setWishlistItems((currentItems) =>
      currentItems.includes(normalizedProductId)
        ? currentItems.filter((itemId) => itemId !== normalizedProductId)
        : [...currentItems, normalizedProductId]
    );
  }, []);

  const cartCount = useMemo(
    () => cartItems.reduce((total, item) => total + item.quantity, 0),
    [cartItems]
  );

  const value = useMemo(
    () => ({
      cartItems,
      wishlistItems,
      cartCount,
      addToCart,
      removeFromCart,
      updateQuantity,
      clearCart,
      addToWishlist,
      removeFromWishlist,
      toggleWishlist,
    }),
    [
      addToCart,
      addToWishlist,
      cartCount,
      cartItems,
      clearCart,
      removeFromCart,
      removeFromWishlist,
      toggleWishlist,
      updateQuantity,
      wishlistItems,
    ]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
